import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import useEOSClient from "../../clients/eos";
import { EOSEndpoint } from "../../clients/eos/index.enum";
import type { EOSRequestConfig } from "../../clients/eos/index.types";
import { useAnalyticsReporting } from "../../utils/hooks/useAnalyticsReporting";
import { withAxiosResponseValidation } from "../common/higher-order-functions/with-axios-response-validation";
import { withInputValidation } from "../common/higher-order-functions/with-input-validation";
import {
	AddVehicleByModelYearResponseSchema,
	AddVehicleByVinResponseSchema,
} from "./validation/AddVehicle/index.schema";
import type {
	AddVehicleByModelYearRawResponse,
	AddVehicleByModelYearResponse,
	AddVehicleByVinRawResponse,
	AddVehicleByVinResponse,
} from "./validation/AddVehicle/index.types";
import type { GetUserVehiclesRawResponse } from "./validation/GetVehicles/index.types";
import { RemoveVehicleRequestSchema } from "./validation/RemoveVehicle/index.schema";
import { UpdateVehicleRequestSchema } from "./validation/UpdateVehicle/index.schema";
import type { UpdateVehicleResponse } from "./validation/UpdateVehicle/index.types";

type AddVehicleByVinPayload = { vin: string };
type AddVehicleByModelYearPayload = { model: string; year: string };
type AddVehiclePayload = AddVehicleByVinPayload | AddVehicleByModelYearPayload;

interface AddVehicle {
	(payload: AddVehicleByVinPayload): Promise<AddVehicleByVinResponse>;
	(
		payload: AddVehicleByModelYearPayload,
	): Promise<AddVehicleByModelYearResponse>;
}

// TODO: Investigate how to clean-up via query-key factory
const initWithCancelUserVehiclesQuery =
	(queryClient: QueryClient) =>
	<T extends (...args: any[]) => any>(fn: T) =>
	async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
		await queryClient.cancelQueries(["user-vehicles"]);
		return fn(...args);
	};

const BASE_PATH = "/v1/customer/vehicles" as const;
/**
 * TODO:
 * - Bring Read & Write UserVehicle Service together when analytics are supported in PersonalizationProvider
 */
export const useReadUserVehicleService = () => {
	const eosClient = useEOSClient();

	/**
	 * TODO:
	 * - Return validated Find Method when API response validation is standardized across all findUserVehicles calls
	 * - Add Analytics Reporting for Success/Failure reporting on this call when Personalization Provider can handle analytics reporting
	 */
	const find = (config?: Partial<EOSRequestConfig>) =>
		eosClient.get<GetUserVehiclesRawResponse>(BASE_PATH, {
			includeAuth: true,
			includeReCaptcha: true,
			service: EOSEndpoint.vehicleManagement,
			...config,
		});

	return { find };
};

export const useWriteUserVehicleService = () => {
	const queryClient = useQueryClient();
	const eosClient = useEOSClient();

	const withCancelUserVehiclesQuery =
		initWithCancelUserVehiclesQuery(queryClient);

	const addVehicleByModelYear = withAxiosResponseValidation(
		({ model, year }: AddVehicleByModelYearPayload) =>
			eosClient.post<AddVehicleByModelYearRawResponse>(
				BASE_PATH,
				{ modelYearList: [{ model, year }], VinList: [] },
				{
					includeAuth: true,
					includeReCaptcha: true,
					service: EOSEndpoint.vehicleManagement,
				},
			),
		AddVehicleByModelYearResponseSchema,
	);

	const addVehicleByVin = withAxiosResponseValidation(
		({ vin }: AddVehicleByVinPayload) =>
			eosClient.post<AddVehicleByVinRawResponse>(
				BASE_PATH,
				{ modelYearList: [], VinList: [{ vin }] },
				{
					includeAuth: true,
					includeReCaptcha: true,
					service: EOSEndpoint.vehicleManagement,
				},
			),
		AddVehicleByVinResponseSchema,
	);

	const add = useAnalyticsReporting(
		withCancelUserVehiclesQuery((payload: AddVehiclePayload) =>
			"vin" in payload
				? addVehicleByVin(payload)
				: addVehicleByModelYear(payload),
		),
		{ name: "addVehicle" },
	) as AddVehicle;

	const remove = useAnalyticsReporting(
		withCancelUserVehiclesQuery(
			withInputValidation(
				({ vehicleId, ...rest }) =>
					eosClient.delete(`${BASE_PATH}/${vehicleId}`, {
						includeAuth: true,
						params: rest,
						service: EOSEndpoint.vehicleManagement,
					}),
				RemoveVehicleRequestSchema,
			),
		),
		{ name: "removeVehicle" },
	);

	const update = useAnalyticsReporting(
		withCancelUserVehiclesQuery(
			withInputValidation(
				({ vehicleId, ...rest }) =>
					eosClient.patch<UpdateVehicleResponse>(
						`${BASE_PATH}/${vehicleId}`,
						rest,
						{
							includeAuth: true,
							includeReCaptcha: true,
							service: EOSEndpoint.vehicleManagement,
						},
					),
				UpdateVehicleRequestSchema,
			),
		),
		{ name: "updateVehicle" },
	);

	return {
		add,
		remove,
		update,
	};
};
